import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usersApi, autoBiddingApi } from "../../api";
import type { IAutoBidGraphResponse } from "../../api/auto-bidding";
import {
  AnalyticsPeriod,
  IAnalyticsResponse,
  IResponseError,
  ITeamMemberRecord,
  ITeamMembersResponse,
  ProfileFilterCriteriaType,
  IProfilesGeneralStatistics,
  IProfilesAnalyticsResponse,
} from "../../data-access";
import { RootState } from "../../store";
import { storeAnalyticsActions } from "../../store/slices/analytics";
import { storeTeamActions } from "../../store/slices/team";
import {
  FormSelect,
  Panel,
  BarChart,
  DateRangePicker,
  ActivityHeatmap,
} from "../../ui";
import type { ILineDataSet } from "../../ui";
import { capitalize, handleFailedRequest } from "../../util";
import { GeneralStatistics } from "./general-statistics";
import { ProfileStatistics } from "./profile-statistics";
import { ByokKeyAlert } from "../byok-key";
import { OnboardingCard } from "../onboarding";
import "./analytics.scss";
import dayjs, { Dayjs } from "dayjs";
import { HiOutlineUserGroup } from "react-icons/hi2";
import { Popover } from "antd";
import { RangePickerProps } from "antd/es/date-picker";
const periodFilter = [
  { value: "current_week", label: "Current Week" },
  { value: "last_week", label: "Last Week" },
  { value: "current_month", label: "Current Month" },
  { value: "last_month", label: "Last Month" },
  { value: "", label: "Please Select" },
  //
  // { value: "last_3_months", label: "Last 3 Months" },
  // { value: "last_year", label: " Last Year" },
];

const profileStatsFilters = [
  {
    name: "current_week",
    label: "Current Week",
  },
  {
    name: "last_week",
    label: "Last Week",
  },
  {
    name: "current_month",
    label: "Current Month",
  },
  {
    name: "last_month",
    label: "Last Month",
  },
  //
  // {
  //   name: "last_3_months",
  //   label: "3 Months",
  // },
  // {
  //   name: "last_6_months",
  //   label: "6 Months",
  // },
  // {
  //   name: "current_year",
  //   label: "Current Year",
  // },
  //
  {
    name: "",
    label: "All Time",
  },
];

const baseClass = "analytics";

type ChartTab = 'funnel' | 'connects' | 'inbound';

const chartTabs: { value: ChartTab; label: string }[] = [
  { value: 'funnel', label: 'Conversion Funnel' },
  { value: 'connects', label: 'Connects Economy' },
  { value: 'inbound', label: 'Inbound vs Outbound' },
];

export const Analytics = (): JSX.Element => {
  const teamMembers = useSelector((state: RootState) => state.team.members);
  const filterPeriod = useSelector(
    (state: RootState) => state.analytics.filter.period
  );
  const filterUserId = useSelector(
    (state: RootState) => state.analytics.filter.userId
  );

  const [isProcessingRequest, setIsProcessingRequest] =
    useState<boolean>(false);
  const [isFetchingJobScoreStats, setIsFetchingJobScoreStats] =
    useState<boolean>(true);
  const [currentUserFilter, setCurrentUserFilter] = useState<string>(
    filterUserId ?? ""
  );
  const [currentPeriodFilter, setCurrentPeriodFilter] = useState<any>(
    filterPeriod || periodFilter[0]?.value
  );
  const [analytics, setAnalytics] = useState<IAnalyticsResponse>({
    total: {
      ai_answers: 0,
      ai_proposals: 0,
      bids: 0,
      opened_jobs: 0,
      replies: 0,
      reply_rate: 0,
      sync_with_crms: 0,
    },
  } as IAnalyticsResponse);
  const [prevAnalytics, setPrevAnalytics] = useState<IAnalyticsResponse | null>(null);
  const [currentProfileStatsCriteria, setCurrentProfileStatsCriteria] =
    useState<any>(profileStatsFilters[0].name as ProfileFilterCriteriaType);
  const [profilesStats, setProfilesStats] =
    useState<IProfilesGeneralStatistics>({} as IProfilesGeneralStatistics);
  const [activeChartTab, setActiveChartTab] = useState<ChartTab>('funnel');

  // Per-panel user filters (default: all members)
  const [proposalsUserFilter, setProposalsUserFilter] = useState<string>("");
  const [trendsUserFilter, setTrendsUserFilter] = useState<string>("");
  const [heatmapUserFilter, setHeatmapUserFilter] = useState<string>("");

  // Per-panel picker open states
  const [proposalsPickerOpen, setProposalsPickerOpen] = useState(false);
  const [trendsPickerOpen, setTrendsPickerOpen] = useState(false);
  const [heatmapPickerOpen, setHeatmapPickerOpen] = useState(false);

  // Proposals & Replies panel data
  const [proposalsRangeDate, setProposalsRangeDate] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('month'),
    dayjs(),
  ]);
  const [proposalsData, setProposalsData] = useState<IAnalyticsResponse | null>(null);
  const [isLoadingProposals, setIsLoadingProposals] = useState(false);

  // Trends & Insights panel data
  const [trendsRangeDate, setTrendsRangeDate] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('month'),
    dayjs(),
  ]);
  const [trendsData, setTrendsData] = useState<IAnalyticsResponse | null>(null);
  const [isLoadingTrends, setIsLoadingTrends] = useState(false);

  // Heatmap data (lazy loaded on scroll)
  const [heatmapData, setHeatmapData] = useState<{ labels: string[]; datasets: any } | null>(null);
  const [isLoadingHeatmap, setIsLoadingHeatmap] = useState(false);
  const [heatmapVisible, setHeatmapVisible] = useState(false);
  const heatmapRef = useRef<HTMLDivElement>(null);

  // Auto-Bidding Activity panel
  const [autoBidGraphData, setAutoBidGraphData] = useState<IAutoBidGraphResponse | null>(null);
  const [isLoadingAutoBid, setIsLoadingAutoBid] = useState(false);
  const [autoBidRangeDate, setAutoBidRangeDate] = useState<[Dayjs | null, Dayjs | null]>([dayjs().startOf('month'), dayjs()]);
  const [autoBidUserFilter, setAutoBidUserFilter] = useState<string>("");
  const [autoBidPickerOpen, setAutoBidPickerOpen] = useState(false);

  const [rangeDate, setRangeDate] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);
  const [rangeStatsDate, setRangeStatsDate] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);

  const dispatch = useDispatch();

  const handlePeriodFilterChange = (period: string | string[]): void => {
    setCurrentPeriodFilter(period as string);
    setRangeStatsDate([null, null]);
    dispatch(storeAnalyticsActions.setPeriod({ period: period as string }));
  };

  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    // Can not select days before today and today
    return current && current >= dayjs().endOf("day");
  };

  const disabledStatDate: RangePickerProps["disabledDate"] = (current) => {
    // Can not select days before today and today
    return current && current >= dayjs().endOf("day");
  };

  useEffect(() => {
    if (teamMembers.length === 0) {
      usersApi
        .getTeamMembers()
        .then((response: ITeamMembersResponse) => {
          dispatch(storeTeamActions.setMembers(response.data));
        })
        .catch((error: IResponseError) => {
          handleFailedRequest(error.status, error.text);
        });
    }

    setTimeout(() => {
      setIsFetchingJobScoreStats(false);
    }, 1500);
  }, [dispatch, teamMembers.length]);

  useEffect(() => {
    if (currentPeriodFilter) {
      setIsProcessingRequest(true);
      const userIdParam = currentUserFilter ? `&user_id=${currentUserFilter}` : "";
      usersApi
        .getUserRangeAnalytics(`time_period=${currentPeriodFilter}&include_previous=1${userIdParam}`)
        .then((response: { data: IAnalyticsResponse }) => {
          setAnalytics(response.data);
          setPrevAnalytics(response.data?.previous_total ? { total: response.data.previous_total } as any : null);
        })
        .catch((error: IResponseError) => {
          handleFailedRequest(error.status, error.text);
        })
        .finally(() => setIsProcessingRequest(false));
    }
  }, [currentPeriodFilter, currentUserFilter]);

  useEffect(() => {
    if (currentProfileStatsCriteria !== null) {
      setIsFetchingJobScoreStats(true);
      usersApi
        .getAnalyticsForProfiles(currentProfileStatsCriteria)
        .then((response: IProfilesAnalyticsResponse) => {
          setProfilesStats(response.data);

        })
        .catch((error: IResponseError) =>
          handleFailedRequest(error.status, error.text)
        )
        .finally(() => setIsFetchingJobScoreStats(false));
    }
  }, [currentProfileStatsCriteria]);

  useEffect(() => {
    if (rangeDate[0] && rangeDate[1]) {
      setIsFetchingJobScoreStats(true);
      const paramsString = `date_from=${rangeDate[0].format(
        "YYYY-MM-DD"
      )}&date_to=${rangeDate[1].format("YYYY-MM-DD")}`;
      usersApi
        .getAnalyticsForProfilesByDate(paramsString)
        .then((response: IProfilesAnalyticsResponse) => {
          setProfilesStats(response.data);

        })
        .catch((error: IResponseError) =>
          handleFailedRequest(error.status, error.text)
        )
        .finally(() => setIsFetchingJobScoreStats(false));
    }
  }, [rangeDate]);

  useEffect(() => {
    if (rangeStatsDate[0] && rangeStatsDate[1]) {
      setIsProcessingRequest(true);
      let paramsString = `date_from=${rangeStatsDate[0].format(
        "YYYY-MM-DD"
      )}&date_to=${rangeStatsDate[1].format("YYYY-MM-DD")}&include_previous=1`;
      if (currentUserFilter) {
        paramsString = `${paramsString}&user_id=${currentUserFilter}`
      }
      usersApi
        .getUserRangeAnalytics(paramsString)
        .then((response: { data: IAnalyticsResponse }) => {
          setAnalytics(response.data);
          setPrevAnalytics(response.data?.previous_total ? { total: response.data.previous_total } as any : null);
        })
        .catch((error: IResponseError) =>
          handleFailedRequest(error.status, error.text)
        )
        .finally(() => setIsProcessingRequest(false));
    }
  }, [rangeStatsDate, currentUserFilter]);

  // Fetch Proposals & Replies + Trends & Insights (shared fetch when params match)
  const buildChartParams = useCallback((dates: [Dayjs | null, Dayjs | null], userFilter: string) => {
    if (!dates[0] || !dates[1]) return null;
    let params = `date_from=${dates[0].format("YYYY-MM-DD")}&date_to=${dates[1].format("YYYY-MM-DD")}`;
    if (userFilter) params += `&user_id=${userFilter}`;
    return params;
  }, []);

  useEffect(() => {
    const proposalsParams = buildChartParams(proposalsRangeDate, proposalsUserFilter);
    const trendsParams = buildChartParams(trendsRangeDate, trendsUserFilter);

    if (!proposalsParams && !trendsParams) return;

    // If params are identical, share one fetch
    if (proposalsParams && proposalsParams === trendsParams) {
      setIsLoadingProposals(true);
      setIsLoadingTrends(true);
      usersApi
        .getUserRangeAnalytics(proposalsParams)
        .then((response: { data: IAnalyticsResponse }) => {
          setProposalsData(response.data);
          setTrendsData(response.data);
        })
        .catch((error: IResponseError) => handleFailedRequest(error.status, error.text))
        .finally(() => { setIsLoadingProposals(false); setIsLoadingTrends(false); });
    } else {
      // Fetch separately when params differ
      if (proposalsParams) {
        setIsLoadingProposals(true);
        usersApi
          .getUserRangeAnalytics(proposalsParams)
          .then((response: { data: IAnalyticsResponse }) => setProposalsData(response.data))
          .catch((error: IResponseError) => handleFailedRequest(error.status, error.text))
          .finally(() => setIsLoadingProposals(false));
      }
      if (trendsParams) {
        setIsLoadingTrends(true);
        usersApi
          .getUserRangeAnalytics(trendsParams)
          .then((response: { data: IAnalyticsResponse }) => setTrendsData(response.data))
          .catch((error: IResponseError) => handleFailedRequest(error.status, error.text))
          .finally(() => setIsLoadingTrends(false));
      }
    }
  }, [proposalsRangeDate, proposalsUserFilter, trendsRangeDate, trendsUserFilter, buildChartParams]);

  // Fetch Auto-Bidding Activity graph
  useEffect(() => {
    if (autoBidRangeDate[0] && autoBidRangeDate[1]) {
      setIsLoadingAutoBid(true);
      let params = `date_from=${autoBidRangeDate[0].format("YYYY-MM-DD")}&date_to=${autoBidRangeDate[1].format("YYYY-MM-DD")}`;
      if (autoBidUserFilter) params += `&user_id=${autoBidUserFilter}`;
      autoBiddingApi
        .getStatsGraph(params)
        .then((response) => setAutoBidGraphData(response))
        .catch(() => setAutoBidGraphData(null))
        .finally(() => setIsLoadingAutoBid(false));
    }
  }, [autoBidRangeDate, autoBidUserFilter]);

  // Lazy load heatmap: observe when panel scrolls into view
  useEffect(() => {
    const el = heatmapRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHeatmapVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Fetch heatmap data: full current year (Jan 1 → Dec 31)
  useEffect(() => {
    if (!heatmapVisible) return;
    setIsLoadingHeatmap(true);
    const yearStart = dayjs().startOf("year").format("YYYY-MM-DD");
    const yearEnd = dayjs().endOf("year").format("YYYY-MM-DD");
    let params = `date_from=${yearStart}&date_to=${yearEnd}`;
    if (heatmapUserFilter) params += `&user_id=${heatmapUserFilter}`;

    usersApi
      .getUserRangeAnalytics(params)
      .then((response: { data: IAnalyticsResponse }) => {
        const g = response.data?.graph;
        if (g?.labels?.length) {
          setHeatmapData({ labels: g.labels, datasets: g.datasets });
        } else {
          setHeatmapData({ labels: [], datasets: {} });
        }
      })
      .catch(() => setHeatmapData({ labels: [], datasets: {} }))
      .finally(() => setIsLoadingHeatmap(false));
  }, [heatmapVisible, heatmapUserFilter]);

  const handleProposalsDatesChange = (data: [Dayjs | null, Dayjs | null]) => {
    if (data && data[0] && data[1]) {
      setProposalsRangeDate(data);
    } else {
      setProposalsRangeDate([null, null]);
    }
  };

  const handleTrendsDatesChange = (data: [Dayjs | null, Dayjs | null]) => {
    if (data && data[0] && data[1]) {
      setTrendsRangeDate(data);
    } else {
      setTrendsRangeDate([null, null]);
    }
  };

  const handleProfileStatsFilterClick = (
    criteria: ProfileFilterCriteriaType
  ): void => {
    if (isFetchingJobScoreStats) return;

    setCurrentProfileStatsCriteria(criteria);
    setRangeDate([null, null]);
  };

  const handleProfileStatsDatesChange = (
    data: [Dayjs | null, Dayjs | null]
  ) => {
    if (data && data[0] && data[1]) {
      setRangeDate(data);
      setCurrentProfileStatsCriteria(null);
    } else {
      setCurrentProfileStatsCriteria(null);
      setRangeDate([null, null]);
    }
  };

  const handleStatsDatesChange = (
    data: [Dayjs | null, Dayjs | null]
  ) => {
    if (data && data[0] && data[1]) {
      setRangeStatsDate(data);
      setCurrentPeriodFilter('');
    } else {
      setRangeStatsDate([null, null]);
      setCurrentPeriodFilter('');
    }
  };

  const proposalsGraphSource = useMemo(() => {
    const g = proposalsData?.graph;
    if (!g?.labels?.length) return { labels: [], datasets: {} as any };
    return g;
  }, [proposalsData]);

  const trendsGraphSource = useMemo(() => {
    const g = trendsData?.graph;
    if (!g?.labels?.length) return { labels: [], datasets: {} as any };
    return g;
  }, [trendsData]);

  // Conversion Funnel: opened_jobs + bids + replies bars, reply rate % line
  const funnelData = useMemo(() => {
    const ds = trendsGraphSource.datasets;
    const line: number[] = (ds.bids || []).map((bid: number, i: number) =>
      bid > 0 ? Math.round(((ds.replies?.[i] || 0) / bid) * 1000) / 10 : 0
    );
    return {
      datasets: [
        { label: 'Jobs Opened', data: ds.opened_jobs || [], backgroundColor: '#F59E0B' },
        { label: 'Proposals', data: ds.bids || [], backgroundColor: '#A78BFA' },
        { label: 'Replies', data: ds.replies || [], backgroundColor: '#60A5FA' },
        { label: 'Offers', data: ds.offers_received || [], backgroundColor: '#EC4899' },
      ],
      lineDataset: { label: 'Reply Rate', data: line, borderColor: '#22C55E', suffix: '%' } as ILineDataSet,
    };
  }, [trendsGraphSource]);

  // Connects Economy: used_connects + bids bars, connects/bid line
  const connectsData = useMemo(() => {
    const ds = trendsGraphSource.datasets;
    const line: number[] = (ds.bids || []).map((bid: number, i: number) =>
      bid > 0 ? Math.round(((ds.used_connects?.[i] || 0) / bid) * 10) / 10 : 0
    );
    return {
      datasets: [
        { label: 'Connects Used', data: ds.used_connects || [], backgroundColor: '#FB923C' },
        { label: 'Proposals', data: ds.bids || [], backgroundColor: '#A78BFA' },
      ],
      lineDataset: { label: 'Connects/Bid', data: line, borderColor: '#38BDF8', suffix: '' } as ILineDataSet,
    };
  }, [trendsGraphSource]);

  // Inbound vs Outbound: bids + invites + direct_messages + offers bars, no line
  const inboundOutboundData = useMemo(() => {
    const ds = trendsGraphSource.datasets;
    return {
      datasets: [
        { label: 'Proposals', data: ds.bids || [], backgroundColor: '#A78BFA' },
        { label: 'Invites', data: ds.invites || [], backgroundColor: '#34D399' },
        { label: 'Direct Messages', data: ds.direct_messages || [], backgroundColor: '#FBBF24' },
        { label: 'Offers', data: ds.offers_received || [], backgroundColor: '#EC4899' },
      ],
    };
  }, [trendsGraphSource]);

  // Auto-Bidding Activity: scanned + submitted + replies + offers bars, submit rate line
  const autoBidChartData = useMemo(() => {
    const ds = autoBidGraphData?.datasets;
    if (!ds) return { datasets: [], lineDataset: undefined };
    const line: number[] = (ds.submitted || []).map((s: number, i: number) =>
      s > 0 ? Math.round(((ds.replies?.[i] || 0) / s) * 1000) / 10 : 0
    );
    return {
      datasets: [
        { label: 'Scanned', data: ds.scanned || [], backgroundColor: '#78778B' },
        { label: 'Submitted', data: ds.submitted || [], backgroundColor: '#60A5FA' },
        { label: 'Replies', data: ds.replies || [], backgroundColor: '#A78BFA' },
        { label: 'Offers', data: ds.offers || [], backgroundColor: '#EC4899' },
      ],
      lineDataset: { label: 'Reply Rate', data: line, borderColor: '#22C55E', suffix: '%' } as ILineDataSet,
    };
  }, [autoBidGraphData]);

  const [summaryPickerOpen, setSummaryPickerOpen] = useState(false);

  const getInitials = (member: ITeamMemberRecord): string => {
    if (member.full_name) {
      const parts = member.full_name.trim().split(/\s+/);
      return (parts[0]?.[0] || "").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
    }
    return member.email.substring(0, 2).toUpperCase();
  };

  const renderUserPicker = (
    filterValue: string,
    setFilter: (v: string) => void,
    isOpen: boolean,
    setOpen: (v: boolean) => void,
    isLoading?: boolean,
  ) => {
    const selected = teamMembers.find((m) => String(m.id) === filterValue);
    const handleSelect = (userId: string) => {
      setFilter(userId);
      setOpen(false);
    };

    return (
      <Popover
        content={
          <div className={`${baseClass}__user-picker-list`}>
            <div
              className={`${baseClass}__user-picker-item ${!filterValue ? "active" : ""}`}
              onClick={() => handleSelect("")}
            >
              <span className={`${baseClass}__user-avatar ${baseClass}__user-avatar--all`}>
                <HiOutlineUserGroup size={16} />
              </span>
              <span>All Members</span>
            </div>
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className={`${baseClass}__user-picker-item ${filterValue === String(member.id) ? "active" : ""}`}
                onClick={() => handleSelect(String(member.id))}
              >
                <span className={`${baseClass}__user-avatar`}>
                  {getInitials(member)}
                </span>
                <span>
                  {member.full_name ?? capitalize(member.email.substring(0, member.email.indexOf("@")))}
                </span>
              </div>
            ))}
          </div>
        }
        trigger="click"
        open={isOpen}
        onOpenChange={setOpen}
        placement="bottomRight"
        overlayClassName={`${baseClass}__user-picker-popover`}
      >
        <div className={`${baseClass}__user-trigger ${isLoading ? 'is-loading' : ''}`}>
          {selected ? (
            <span className={`${baseClass}__user-avatar`}>
              {getInitials(selected)}
            </span>
          ) : (
            <span className={`${baseClass}__user-avatar ${baseClass}__user-avatar--all`}>
              <HiOutlineUserGroup size={16} />
            </span>
          )}
        </div>
      </Popover>
    );
  };

  // Activity Summary picker also saves to Redux
  const handleSummaryUserChange = (userId: string) => {
    setCurrentUserFilter(userId);
    dispatch(storeAnalyticsActions.setUserId({ userId }));
  };

  return (
    <div className={baseClass}>
      <ByokKeyAlert />
      <OnboardingCard />
      <Panel
        title="Activity Summary"
        className={`${baseClass}-panel`}
        nodeInTitleRow={
          <div className={`${baseClass}__filters`}>
            {renderUserPicker(currentUserFilter, handleSummaryUserChange, summaryPickerOpen, setSummaryPickerOpen, isProcessingRequest)}
            <ul className={`${baseClass}__period-tabs`}>
              {periodFilter
                .filter((p) => p.value !== "")
                .map((p) => (
                  <li
                    key={p.value}
                    className={currentPeriodFilter === p.value ? "active" : ""}
                    onClick={() =>
                      !isProcessingRequest && handlePeriodFilterChange(p.value)
                    }
                  >
                    {p.label}
                  </li>
                ))}
            </ul>
            <div className={`${baseClass}__period-select`}>
              <FormSelect
                options={periodFilter.filter((p) => p.value !== "")}
                value={currentPeriodFilter}
                onChange={(val) =>
                  !isProcessingRequest && handlePeriodFilterChange(val)
                }
              />
            </div>
            <DateRangePicker
              defaultValue={rangeStatsDate}
              disabledDate={disabledStatDate}
              onChange={(event) => handleStatsDatesChange(event)}
            />
          </div>
        }
      >
        <GeneralStatistics
          isLoading={isProcessingRequest}
          stats={analytics?.total}
          prevStats={prevAnalytics?.total}
        />
      </Panel>
      <Panel
        title="Performance by Profile"
        className="profile-stats-panel"
        nodeInTitleRow={
          <div className={`${baseClass}__filters`}>
            <ul className={`${baseClass}__period-tabs`}>
              {profileStatsFilters.map((filter) => (
                <li
                  key={filter.name}
                  className={
                    currentProfileStatsCriteria === filter.name ? "active" : ""
                  }
                  onClick={() =>
                    !isFetchingJobScoreStats &&
                    handleProfileStatsFilterClick(
                      filter.name as ProfileFilterCriteriaType
                    )
                  }
                >
                  {filter.label}
                </li>
              ))}
            </ul>
            <div className={`${baseClass}__period-select`}>
              <FormSelect
                options={profileStatsFilters.map((f) => ({
                  value: f.name,
                  label: f.label,
                }))}
                value={currentProfileStatsCriteria}
                onChange={(val) =>
                  !isFetchingJobScoreStats &&
                  handleProfileStatsFilterClick(
                    val as ProfileFilterCriteriaType
                  )
                }
              />
            </div>
            <DateRangePicker
              defaultValue={rangeDate}
              disabledDate={disabledDate}
              onChange={(event) => handleProfileStatsDatesChange(event)}
            />
          </div>
        }
      >
        <ProfileStatistics
          isLoading={isFetchingJobScoreStats}
          data={profilesStats}
          filterPeriod={currentProfileStatsCriteria}
          rangeDate={rangeDate}
        />
      </Panel>
      <Panel
        title="Proposals & Replies"
        className="proposals-panel"
        nodeInTitleRow={
          <div className={`${baseClass}__filters`}>
            {renderUserPicker(proposalsUserFilter, setProposalsUserFilter, proposalsPickerOpen, setProposalsPickerOpen, isLoadingProposals)}
            <DateRangePicker
              defaultValue={proposalsRangeDate}
              disabledDate={disabledStatDate}
              onChange={(event) => handleProposalsDatesChange(event)}
            />
          </div>
        }
      >
        <BarChart
          isLoading={isLoadingProposals}
          labels={proposalsGraphSource.labels}
          replyRate={proposalsGraphSource.datasets.reply_rate}
          datasets={[
            { label: 'Proposals', data: proposalsGraphSource.datasets.bids, backgroundColor: '#A78BFA' },
            { label: 'Replies', data: proposalsGraphSource.datasets.replies, backgroundColor: '#60A5FA' },
            { label: 'Offers', data: proposalsGraphSource.datasets.offers_received, backgroundColor: '#EC4899' },
          ]}
        />
      </Panel>
      <Panel
        title="Trends & Insights"
        className="chart-panel"
        nodeInTitleRow={
          <div className={`${baseClass}__filters`}>
            {renderUserPicker(trendsUserFilter, setTrendsUserFilter, trendsPickerOpen, setTrendsPickerOpen, isLoadingTrends)}
            <ul className={`${baseClass}__chart-tabs`}>
              {chartTabs.map((tab) => (
                <li
                  key={tab.value}
                  className={activeChartTab === tab.value ? 'active' : ''}
                  onClick={() => setActiveChartTab(tab.value)}
                >
                  {tab.label}
                </li>
              ))}
            </ul>
            <div className={`${baseClass}__period-select`}>
              <FormSelect
                options={chartTabs.map((t) => ({ value: t.value, label: t.label }))}
                value={activeChartTab}
                onChange={(val) => setActiveChartTab(val as ChartTab)}
              />
            </div>
            <DateRangePicker
              defaultValue={trendsRangeDate}
              disabledDate={disabledStatDate}
              onChange={(event) => handleTrendsDatesChange(event)}
            />
          </div>
        }
      >
        {activeChartTab === 'funnel' && (
          <BarChart
            isLoading={isLoadingTrends}
            labels={trendsGraphSource.labels}
            datasets={funnelData.datasets}
            lineDataset={funnelData.lineDataset}
          />
        )}
        {activeChartTab === 'connects' && (
          <BarChart
            isLoading={isLoadingTrends}
            labels={trendsGraphSource.labels}
            datasets={connectsData.datasets}
            lineDataset={connectsData.lineDataset}
          />
        )}
        {activeChartTab === 'inbound' && (
          <BarChart
            isLoading={isLoadingTrends}
            labels={trendsGraphSource.labels}
            datasets={inboundOutboundData.datasets}
          />
        )}
      </Panel>
      <Panel
        title="Auto-Bidding Activity"
        className="chart-panel"
        nodeInTitleRow={
          <div className={`${baseClass}__filters`}>
            {renderUserPicker(autoBidUserFilter, setAutoBidUserFilter, autoBidPickerOpen, setAutoBidPickerOpen, isLoadingAutoBid)}
            <DateRangePicker
              defaultValue={autoBidRangeDate}
              disabledDate={disabledStatDate}
              onChange={(dates) => {
                if (dates?.[0] && dates?.[1]) setAutoBidRangeDate(dates as [Dayjs, Dayjs]);
              }}
            />
          </div>
        }
      >
        <BarChart
          isLoading={isLoadingAutoBid}
          labels={autoBidGraphData?.labels || []}
          datasets={autoBidChartData.datasets}
          lineDataset={autoBidChartData.lineDataset}
        />
      </Panel>
      <div ref={heatmapRef} />
      <Panel
        title="Activity Heatmap"
        className="heatmap-panel"
        nodeInTitleRow={
          renderUserPicker(heatmapUserFilter, setHeatmapUserFilter, heatmapPickerOpen, setHeatmapPickerOpen, isLoadingHeatmap)
        }
      >
        <ActivityHeatmap
          isLoading={isLoadingHeatmap}
          labels={heatmapData?.labels || []}
          datasets={heatmapData?.datasets || {}}
        />
      </Panel>
    </div>
  );
};

export default Analytics;
